<script setup lang="ts">
import MultipleCountWidget from '@/modules/dashboard/components/MultipleCountWidget.vue'
import type { IMultipleCountWidget } from '@/modules/dashboard/types/IMultipleCountWidget'
import type { IImportCellCorrection } from '../types/IImportCellCorrection'
import type { IImportColumn } from '../types/IImportColumn'
import type { IImportColumnSelection } from '../types/IImportColumnSelection'
import type { IImportPreviewRow } from '../types/IImportPreviewRow'
import type { IImportValidateResponse } from '../types/IImportValidateResponse'
import type { TImportRowFilter } from '../types/TImportRowFilter'
import ImportFileChip from './ImportFileChip.vue'
import ImportPreviewTreeNode from './ImportPreviewTreeNode.vue'

const props = defineProps<{
  columns?: IImportColumn[]
  fileName: string
  fileSize: number
  validation: IImportValidateResponse
  selections: IImportColumnSelection[]
  rows: IImportPreviewRow[]
  page: number
  totalPages: number
  pageSize: number
  rowsCount: number
  filter: TImportRowFilter
  saving: boolean
  structure: 'flat' | 'tree'
  paginated: boolean
  rowsNotice: string | null
  allowCellEdit: boolean
}>()

const emit = defineEmits<{
  remove: []
  back: []
  save: []
  'update:filter': [value: TImportRowFilter]
  'update:page': [value: number]
  'update:page-size': [value: number]
  'edit-cell': [value: IImportCellCorrection]
}>()

const mappedColumns = computed(() =>
  props.selections
    .filter(item => item.canonical)
    .map(item => item.canonical as string),
)

const warningRowsCount = computed(() => props.validation.warning_rows_count || 0)
const skippedRowsCount = computed(() => props.validation.skipped_rows_count || 0)

const showFilters = computed(() =>
  props.validation.error_rows_count > 0 || warningRowsCount.value > 0 || skippedRowsCount.value > 0,
)

// Сообщение бэка по пропущенной строке («уже в системе, данные совпадают») по номеру строки
const skippedMessageByRow = computed(() => {
  const map = new Map<number, string>()
  for (const issue of props.validation.skipped || []) {
    if (issue.row != null && issue.message)
      map.set(issue.row, issue.message)
  }
  return map
})

const errorLabel = computed(() =>
  props.validation.error_rows_count > 0 ? 'Ошибки данных' : 'Ошибки',
)

function rowsLabel(count: number) {
  const abs = Math.abs(count) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20)
    return `${count} строк`
  if (last > 1 && last < 5)
    return `${count} строки`
  if (last === 1)
    return `${count} строка`
  return `${count} строк`
}

const saveLabel = computed(() =>
  props.validation.error_rows_count > 0
    ? 'Сохранить данные с ошибками и завершить импорт'
    : 'Сохранить данные и завершить импорт',
)

const widgetData = computed<IMultipleCountWidget>(() => ({
  id: 0,
  name: 'Результат загрузки',
  description: '',
  icon: '',
  show_icon: false,
  order: 0,
  widget_type: 'multiple_count',
  data: [
    {
      label: 'Загружено',
      period: '',
      attribute: 'Загружено',
      value: props.validation.total_rows,
      value_text: rowsLabel(props.validation.total_rows),
      icon: 'ri-arrow-down-circle-line',
    },
    {
      label: 'Успешно',
      period: '',
      attribute: 'Успешно',
      value: props.validation.valid_rows_count,
      value_text: rowsLabel(props.validation.valid_rows_count),
      icon: 'ri-checkbox-circle-line',
    },
    {
      label: errorLabel.value,
      period: '',
      attribute: errorLabel.value,
      value: props.validation.error_rows_count,
      icon: 'ri-alert-line',
    },
    // Предупреждения и пропуски показываем, только если они есть
    ...(warningRowsCount.value > 0
      ? [{
          label: 'Предупреждения',
          period: '',
          attribute: 'Предупреждения',
          value: warningRowsCount.value,
          value_text: rowsLabel(warningRowsCount.value),
          icon: 'ri-error-warning-line',
        }]
      : []),
    ...(skippedRowsCount.value > 0
      ? [{
          label: 'Пропущено',
          period: '',
          attribute: 'Пропущено',
          value: skippedRowsCount.value,
          value_text: rowsLabel(skippedRowsCount.value),
          icon: 'ri-skip-forward-line',
        }]
      : []),
  ],
}))

function cellValue(row: IImportPreviewRow, column: string) {
  const value = row.data[column]
  return value == null ? '' : String(value)
}

function cellHasError(row: IImportPreviewRow, column: string) {
  return row.errors.some(issue => issue.column === column)
}

const editingCell = ref<string | null>(null)
const editingValue = ref('')
const collapsedNodes = ref<string[]>([])

function toggleNode(key: string) {
  collapsedNodes.value = collapsedNodes.value.includes(key)
    ? collapsedNodes.value.filter(item => item !== key)
    : [...collapsedNodes.value, key]
}

function startTreeEdit(payload: { key: string, value: string }) {
  editingCell.value = payload.key
  editingValue.value = payload.value
}

function cellKey(row: number, column: string) {
  return `${row}:${column}`
}

function getColumnDataType(columnName: string): string | undefined {
  return props.columns?.find(c => c.name === columnName)?.data_type
}

function isPhoneColumn(columnName: string): boolean {
  return getColumnDataType(columnName) === 'phone'
}

function formatPhoneValue(input: string): string {
  if (!input) return ''
  const trimmed = input.trim()
  if (!trimmed) return ''

  const digits = input.replace(/\D/g, '')
  if (!digits) return ''

  let localDigits = ''
  if (digits.startsWith('7') || digits.startsWith('8')) {
    localDigits = digits.slice(1, 11)
  } else {
    localDigits = digits.slice(0, 10)
  }

  if (localDigits.length === 0) {
    return '+7 ('
  }
  if (localDigits.length <= 3) {
    return `+7 (${localDigits}`
  }
  if (localDigits.length <= 6) {
    return `+7 (${localDigits.slice(0, 3)}) ${localDigits.slice(3)}`
  }
  if (localDigits.length <= 8) {
    return `+7 (${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`
  }
  return `+7 (${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6, 8)}-${localDigits.slice(8, 10)}`
}

function normalizePhoneForSave(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (!digits) return ''
  let localDigits = ''
  if (digits.startsWith('7') || digits.startsWith('8')) {
    localDigits = digits.slice(1, 11)
  } else {
    localDigits = digits.slice(0, 10)
  }
  if (!localDigits) return ''
  return `+7${localDigits}`
}

function isEditing(row: number | null, column: string) {
  return row != null && editingCell.value === cellKey(row, column)
}

function startEdit(row: IImportPreviewRow, column: string) {
  if (row.row == null)
    return
  editingCell.value = cellKey(row.row, column)
  const raw = cellValue(row, column)
  if (isPhoneColumn(column)) {
    editingValue.value = raw ? formatPhoneValue(raw) : ''
  } else {
    editingValue.value = raw
  }
}

function onCellKeyDown(event: KeyboardEvent, row: IImportPreviewRow, column: string) {
  if (event.key === 'Enter') {
    event.preventDefault()
    saveEdit(row, column)
    return
  }

  if (event.key === 'Backspace' && isPhoneColumn(column)) {
    const target = event.target as HTMLInputElement
    if (editingValue.value === '+7 (' || editingValue.value === '+7' || target.value.length <= 4) {
      event.preventDefault()
      editingValue.value = ''
      target.value = ''
    }
  }
}

function onCellInputField(event: Event, column: string) {
  const target = event.target as HTMLInputElement
  if (!target) return
  if (isPhoneColumn(column)) {
    if (!target.value || !target.value.trim()) {
      editingValue.value = ''
      target.value = ''
      return
    }
    const formatted = formatPhoneValue(target.value)
    editingValue.value = formatted
    target.value = formatted
  } else {
    editingValue.value = target.value
  }
}

function placeCaret(input: HTMLInputElement) {
  input.focus()
  const end = input.value.length
  input.setSelectionRange(end, end)
}

function onCellInput(el: unknown) {
  if (!(el instanceof HTMLInputElement))
    return
  placeCaret(el)
  requestAnimationFrame(() => placeCaret(el))
}

function saveEdit(row: IImportPreviewRow, column: string) {
  if (row.row == null)
    return
  const valueToSave = isPhoneColumn(column)
    ? normalizePhoneForSave(editingValue.value)
    : editingValue.value
  emit('edit-cell', { row: row.row, column, value: valueToSave })
  editingCell.value = null
}

function saveTreeEdit(payload: IImportCellCorrection) {
  editingCell.value = null
  emit('edit-cell', payload)
}

function cancelEdit() {
  editingCell.value = null
}

function rowMessages(row: IImportPreviewRow) {
  return [...new Set(row.errors.map(issue => issue.message).filter(Boolean))].join(' ')
}

function rowWarningMessages(row: IImportPreviewRow) {
  return [...new Set((row.warnings || []).map(issue => issue.message).filter(Boolean))].join(' ')
}

function rowSkippedMessage(row: IImportPreviewRow) {
  if (!row.is_skipped)
    return ''
  return (row.row != null && skippedMessageByRow.value.get(row.row)) || 'Запись уже в системе, данные совпадают — строка будет пропущена'
}

// Иконка колонки «Проверка»: ошибка > пропуск > предупреждение > успех
function rowStatusIcon(row: IImportPreviewRow): { icon: string, color: string } {
  if (row.errors.length)
    return { icon: 'ri-alert-line', color: 'error' }
  if (row.is_skipped)
    return { icon: 'ri-skip-forward-line', color: 'secondary' }
  if (row.warnings?.length)
    return { icon: 'ri-error-warning-line', color: 'warning' }
  return { icon: 'ri-checkbox-circle-line', color: 'success' }
}

/** Тот же ключ, что VDataTable отдаёт в VDataTableFooter. */
const dataTablePaginationKey = Symbol.for('vuetify:data-table-pagination')

const pageModel = computed({
  get: () => props.page,
  set: (value: number) => {
    emit('update:page', value)
  },
})

const pageSizeModel = computed({
  get: () => props.pageSize,
  set: (value: number) => {
    emit('update:page-size', value)
  },
})

const itemsLength = computed(() => props.rowsCount)

const startIndex = computed(() =>
  itemsLength.value ? props.pageSize * (props.page - 1) : 0,
)

const stopIndex = computed(() =>
  itemsLength.value ? Math.min(itemsLength.value, startIndex.value + props.pageSize) : 0,
)

const pageCount = computed(() => Math.max(props.totalPages, 1))

function setPage(value: number) {
  pageModel.value = Math.min(Math.max(value, 1), pageCount.value)
}

provide(dataTablePaginationKey, {
  page: pageModel,
  itemsPerPage: pageSizeModel,
  startIndex,
  stopIndex,
  pageCount,
  itemsLength,
  nextPage: () => setPage(props.page + 1),
  prevPage: () => setPage(props.page - 1),
  setPage,
  setItemsPerPage: (value: number) => {
    pageSizeModel.value = value
  },
})

const hideNextButton = computed(() => props.page >= props.totalPages)
</script>

<template>
  <div class="import-preview d-flex flex-column ga-6">
    <ImportFileChip
      :name="fileName"
      :size="fileSize"
      @remove="emit('remove')"
    />

    <VCard class="import-result-card pa-4 d-flex flex-column ga-4">
      <span class="text-h5">Результат загрузки</span>
      <MultipleCountWidget
        flat
        :bordered="false"
        plain-value
        :widget-data="widgetData"
      />
    </VCard>

    <VCard class="import-preview-card pa-4 d-flex flex-column ga-4">
      <div class="d-flex align-center justify-space-between ga-3 flex-wrap">
        <span class="text-h5">Предпросмотр данных</span>
        <div v-if="showFilters" class="d-flex align-center ga-2">
          <VBtn
            class="import-filter-btn"
            :class="{ 'import-filter-btn--active': filter === 'all' }"
            variant="outlined"
            color="secondary"
            :prepend-icon="filter === 'all' ? 'ri-filter-fill' : 'ri-filter-2-line'"
            @click="emit('update:filter', 'all')"
          >
            Все
          </VBtn>
          <VBtn
            v-if="validation.error_rows_count > 0"
            class="import-filter-btn import-filter-btn--errors"
            :class="{ 'import-filter-btn--active': filter === 'errors' }"
            variant="outlined"
            color="error"
            :prepend-icon="filter === 'errors' ? 'ri-filter-fill' : 'ri-filter-2-line'"
            @click="emit('update:filter', 'errors')"
          >
            С ошибками
          </VBtn>
          <VBtn
            v-if="warningRowsCount > 0"
            class="import-filter-btn import-filter-btn--warnings"
            :class="{ 'import-filter-btn--active': filter === 'warnings' }"
            variant="outlined"
            color="warning"
            :prepend-icon="filter === 'warnings' ? 'ri-filter-fill' : 'ri-filter-2-line'"
            @click="emit('update:filter', 'warnings')"
          >
            С предупреждениями
          </VBtn>
          <VBtn
            v-if="skippedRowsCount > 0"
            class="import-filter-btn"
            :class="{ 'import-filter-btn--active': filter === 'skipped' }"
            variant="outlined"
            color="secondary"
            :prepend-icon="filter === 'skipped' ? 'ri-filter-fill' : 'ri-filter-2-line'"
            @click="emit('update:filter', 'skipped')"
          >
            Пропущенные
          </VBtn>
        </div>
      </div>

      <div v-if="rowsNotice" class="text-error">
        {{ rowsNotice }}
      </div>
      <div v-else class="import-preview-scroll">
      <div v-if="structure === 'tree'" class="d-flex flex-column">
        <ImportPreviewTreeNode
          v-for="node in rows"
          :key="String(node.path || node.row || node.name || '')"
          :node="node"
          :columns="mappedColumns"
          :collapsed="collapsedNodes"
          :allow-cell-edit="allowCellEdit"
          :editing-key="editingCell"
          :editing-value="editingValue"
          @toggle="toggleNode"
          @start-edit="startTreeEdit"
          @update:editing-value="editingValue = $event"
          @save="saveTreeEdit"
          @cancel="editingCell = null"
        />
        <div v-if="!rows.length" class="pa-4 text-medium-emphasis">
          Нет строк для предпросмотра
        </div>
      </div>
      <div v-else-if="rows.length">
        <table class="import-plain-table">
          <thead>
            <tr>
              <th
                v-for="column in mappedColumns"
                :key="column"
              >
                {{ column }}
              </th>
              <th>Проверка</th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(row, rIdx) in rows"
              :key="row.row ?? rIdx"
            >
              <tr>
                <td
                  v-for="column in mappedColumns"
                  :key="column"
                >
                  <div
                    v-if="cellHasError(row, column)"
                    class="import-cell-error d-flex align-center justify-space-between"
                  >
                    <input
                      v-if="isEditing(row.row, column)"
                      :ref="onCellInput"
                      :value="editingValue"
                      :type="isPhoneColumn(column) ? 'tel' : 'text'"
                      :maxlength="isPhoneColumn(column) ? 18 : undefined"
                      class="import-cell-input text-error"
                      @input="onCellInputField($event, column)"
                      @keydown="onCellKeyDown($event, row, column)"
                    >
                    <span
                      v-else
                      class="text-error"
                    >{{ cellValue(row, column) }}</span>
                    <div class="d-flex align-center">
                      <template v-if="isEditing(row.row, column)">
                        <VTooltip location="top" text="Сохранить">
                          <template #activator="{ props: tooltipProps }">
                            <VIconBtn
                              v-bind="tooltipProps"
                              :rounded="false"
                              :size="32"
                              icon="ri-check-line"
                              variant="text"
                              @click="saveEdit(row, column)"
                            />
                          </template>
                        </VTooltip>
                        <VTooltip location="top" text="Отменить">
                          <template #activator="{ props: tooltipProps }">
                            <VIconBtn
                              v-bind="tooltipProps"
                              :rounded="false"
                              :size="32"
                              icon="ri-close-line"
                              variant="text"
                              @click="cancelEdit"
                            />
                          </template>
                        </VTooltip>
                      </template>
                      <VIconBtn
                        v-else
                        :rounded="false"
                        :size="32"
                        icon="ri-pencil-line"
                        variant="text"
                        @mousedown.prevent
                        @click="startEdit(row, column)"
                      />
                    </div>
                  </div>
                  <template v-else>
                    {{ cellValue(row, column) }}
                  </template>
                </td>
                <td>
                  <VIcon
                    :icon="rowStatusIcon(row).icon"
                    :color="rowStatusIcon(row).color"
                  />
                </td>
              </tr>
              <tr v-if="rowMessages(row)" class="import-row-error">
                <td
                  :colspan="mappedColumns.length + 1"
                  class="text-error text-body-2"
                >
                  {{ rowMessages(row) }}
                </td>
              </tr>
              <tr v-if="rowWarningMessages(row)" class="import-row-error">
                <td
                  :colspan="mappedColumns.length + 1"
                  class="text-warning text-body-2"
                >
                  {{ rowWarningMessages(row) }}
                </td>
              </tr>
              <tr v-if="rowSkippedMessage(row)" class="import-row-error">
                <td
                  :colspan="mappedColumns.length + 1"
                  class="text-medium-emphasis text-body-2"
                >
                  {{ rowSkippedMessage(row) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else class="pa-4 text-medium-emphasis">
        Нет строк для предпросмотра
      </div>
      </div>
    </VCard>

    <div class="import-preview-bar d-flex align-center ga-3">
      <VBtn
        class="import-step-btn flex-shrink-0"
        variant="outlined"
        color="primary"
        prepend-icon="ri-arrow-left-line"
        :disabled="saving"
        @click="emit('back')"
      >
        Назад
      </VBtn>
      <div
        v-if="paginated && structure !== 'tree'"
        class="custom-data-table-footer flex-grow-1"
        :class="{ 'hide-next-button': hideNextButton }"
      >
        <VDataTableFooter
          show-current-page
          :items-per-page-options="[25, 50, 100]"
        />
      </div>
      <div v-else class="flex-grow-1" />
      <VBtn
        class="flex-shrink-0"
        color="primary"
        prepend-icon="ri-check-line"
        :loading="saving"
        @click="emit('save')"
      >
        {{ saveLabel }}
      </VBtn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.import-preview {
  flex: 1;
  min-block-size: 0;
  overflow: hidden;

  > :not(.import-preview-card) {
    flex-shrink: 0;
  }
}

.import-preview-card {
  flex: 1;
  min-block-size: 0;
  overflow: hidden;

  > :not(.import-preview-scroll) {
    flex-shrink: 0;
  }
}

.import-preview-scroll {
  flex: 1;
  min-block-size: 0;
  overflow: auto;
}

.import-result-card {
  max-width: 600px;
}

.import-filter-btn.v-btn {
  background: transparent;
}

.import-filter-btn--active.v-btn:not(.import-filter-btn--errors):not(.import-filter-btn--warnings) {
  background: rgba(var(--v-theme-secondary), var(--v-selected-opacity));
}

.import-filter-btn--errors.import-filter-btn--active.v-btn {
  background: rgba(var(--v-theme-error), var(--v-selected-opacity));
}

.import-filter-btn--warnings.import-filter-btn--active.v-btn {
  background: rgba(var(--v-theme-warning), var(--v-selected-opacity));
}

.import-step-btn {
  inline-size: 12rem;
  min-inline-size: 12rem;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}

.import-plain-table {
  border-collapse: collapse;
  width: 100%;

  th {
    --v-overlay-scrim-opacity: 1;

    background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity));
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    font-size: 15px;
    font-weight: 500;
    inset-block-start: 0;
    letter-spacing: 0;
    line-height: 22px;
    padding: 0.75rem 1rem;
    position: sticky;
    text-align: start;
    white-space: nowrap;
    z-index: 1;
  }

  td {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    padding: 0.75rem 1rem;
    text-align: start;

    &.text-error {
      color: rgb(var(--v-theme-error));
    }
  }

  .import-cell-error {
    inline-size: 100%;
  }

  .import-cell-input {
    background: transparent;
    border: 0;
    caret-color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    color: inherit;
    flex: 1 1 auto;
    font: inherit;
    min-inline-size: 0;
    outline: none;
  }

  tr.import-row-error td {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding-top: 0;
  }

  th:not(:last-child)::after {
    background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
    content: '';
    height: 14px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
  }
}
</style>
